import { deleteChatDocument } from '@/services/documents/chatDocumentsApi'

/**
 * In-flight attachment uploads for **one** composer.
 *
 * Per-instance rather than module-global: a send must wait only on its own
 * uploads, not on ones started by a different mounted composer.
 *
 * Cancelling is two-sided on purpose. Aborting stops the browser's leg, but the
 * server may already have committed — and a disconnected request is not
 * guaranteed to cancel server-side — so the entry also carries the handles
 * needed to delete whatever landed: the document id once known, and before then
 * the idempotency key, which exists from the upload's first round trip.
 */
export function createAttachmentUploads() {
  const attachments = new Map()
  const pending = new Map()

  return {
    track(id, entry) {
      const tracked = entry.promise.finally(() => {
        if (pending.get(id) === tracked) pending.delete(id)
      })
      attachments.set(id, { ...entry, promise: tracked })
      pending.set(id, tracked)
      return tracked
    },

    /** Bind the chat once it exists; false means the attachment was cancelled. */
    bindChat(id, chatId) {
      const entry = attachments.get(id)
      if (!entry) return false
      attachments.set(id, { ...entry, chatId: chatId ?? null })
      return true
    },

    /** Record the document id as soon as the upload yields one. */
    settle(id, documentId) {
      const entry = attachments.get(id)
      if (entry) attachments.set(id, { ...entry, documentId })
    },

    cancel(id) {
      const entry = attachments.get(id)
      if (!entry) return
      attachments.delete(id)
      pending.delete(id)
      entry.controller?.abort()
      if (!entry.chatId) return { chatId: null, cleanup: null }
      const cleanup = deleteChatDocument({
        chatId: entry.chatId,
        documentId: entry.documentId,
        idempotencyKey: entry.idempotencyKey,
      })
      return { chatId: entry.chatId, cleanup }
    },

    /** Resolve once every in-flight upload has settled. */
    async awaitAll() {
      const inflight = [...pending.values()]
      return inflight.length ? Promise.all(inflight) : []
    },
  }
}
